import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Organization-owned ("internal") contacts.
 *
 * Internal contacts are Contact rows scoped to an Organization directly,
 * with `companyId IS NULL`. They represent home-org people — salespeople,
 * internal ops leads, etc. — who aren't tied to a customer or supplier.
 *
 * The external (Customer/Supplier/Company-scoped) contact procedures live
 * in `contacts.contract.ts`; this module is intentionally separate so each
 * contract carries its own validation shape and the handlers can't
 * accidentally cross-update between scopes.
 */

const UUIDSchema = z.uuid();

export const OrganizationContactSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  title: z.string().nullable(),
  isPrimary: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const ListOrganizationContactsInputSchema = z.object({});
export const ListOrganizationContactsResponseSchema = z.object({
  contacts: z.array(OrganizationContactSchema),
});

export const GetOrganizationContactInputSchema = z.object({
  id: UUIDSchema,
});
export const GetOrganizationContactResponseSchema = z.object({
  contact: OrganizationContactSchema,
});

export const CreateOrganizationContactInputSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email().nullable().optional(),
  phone: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  isPrimary: z.boolean().optional(),
});
export const CreateOrganizationContactResponseSchema = z.object({
  contact: OrganizationContactSchema,
});

export const UpdateOrganizationContactInputSchema = z.object({
  id: UUIDSchema,
  name: z.string().min(1).optional(),
  email: z.string().email().nullable().optional(),
  phone: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  isPrimary: z.boolean().optional(),
});
export const UpdateOrganizationContactResponseSchema = z.object({
  contact: OrganizationContactSchema,
});

export const DeleteOrganizationContactInputSchema = z.object({
  id: UUIDSchema,
});
export const DeleteOrganizationContactResponseSchema = z.object({
  success: z.boolean(),
});

export const organizationContactsContracts = {
  list: oc
    .input(ListOrganizationContactsInputSchema)
    .output(ListOrganizationContactsResponseSchema)
    .route({
      method: "GET",
      summary: "List internal contacts",
      description:
        "Lists the organization's own internal contacts, such as salespeople and ops leads, who are not tied to a customer or supplier.",
    }),
  get: oc
    .input(GetOrganizationContactInputSchema)
    .output(GetOrganizationContactResponseSchema)
    .route({
      method: "GET",
      summary: "Get an internal contact",
      description:
        "Returns one internal organization contact with name, email, phone, title, and primary flag.",
    }),
  create: oc
    .input(CreateOrganizationContactInputSchema)
    .output(CreateOrganizationContactResponseSchema)
    .route({
      method: "POST",
      summary: "Create an internal contact",
      description:
        "Adds an internal organization contact (not tied to a customer or supplier) with name, email, phone, title, and primary flag.",
    }),
  update: oc
    .input(UpdateOrganizationContactInputSchema)
    .output(UpdateOrganizationContactResponseSchema)
    .route({
      method: "POST",
      summary: "Update an internal contact",
      description:
        "Edits an internal organization contact's name, email, phone, title, or primary flag.",
    }),
  delete: oc
    .input(DeleteOrganizationContactInputSchema)
    .output(DeleteOrganizationContactResponseSchema)
    .route({
      method: "POST",
      summary: "Delete an internal contact",
      description: "Deletes an internal organization contact.",
    }),
} as const;

export type OrganizationContact = z.infer<typeof OrganizationContactSchema>;
export type ListOrganizationContactsInput = z.infer<
  typeof ListOrganizationContactsInputSchema
>;
export type ListOrganizationContactsResponse = z.infer<
  typeof ListOrganizationContactsResponseSchema
>;
export type GetOrganizationContactInput = z.infer<
  typeof GetOrganizationContactInputSchema
>;
export type GetOrganizationContactResponse = z.infer<
  typeof GetOrganizationContactResponseSchema
>;
export type CreateOrganizationContactInput = z.infer<
  typeof CreateOrganizationContactInputSchema
>;
export type CreateOrganizationContactResponse = z.infer<
  typeof CreateOrganizationContactResponseSchema
>;
export type UpdateOrganizationContactInput = z.infer<
  typeof UpdateOrganizationContactInputSchema
>;
export type UpdateOrganizationContactResponse = z.infer<
  typeof UpdateOrganizationContactResponseSchema
>;
export type DeleteOrganizationContactInput = z.infer<
  typeof DeleteOrganizationContactInputSchema
>;
export type DeleteOrganizationContactResponse = z.infer<
  typeof DeleteOrganizationContactResponseSchema
>;
